import React, { useEffect, useState, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  fetchMeterBrands,
  fetchQnoms,
  fetchDiameters,
  createMeterReport,
  uploadMeterReportImage,
  replaceMeter,
  fetchMeterReplacementDraft,
  deleteMeterReplacementDraft,
} from "../../data/fetchData";
import Popup from "reactjs-popup";
import { FaTimesCircle } from "react-icons/fa";
import { useNotification } from "../../context/NotificationContext";
import { formatDecimal, normalizeDecimal } from "../../utils/formatters";

const TYPE_LABELS = {
  MAIN_GAUGE: "Põhiarvesti",
  SUB_GAUGE: "Alamarvesti",
  WATERING: "Kastmine",
};

export default function Replace3() {
  const navigate = useNavigate();
  const { showSuccess, showError } = useNotification();
  const [data, setData] = useState({});
  const [brandOptions, setBrandOptions] = useState([]);
  const [qnOptions, setQnOptions] = useState([]);
  const [diamOptions, setDiamOptions] = useState([]);
  const [searchParams] = useSearchParams();
  const draftParam = searchParams.get("draft");
  const draftId = draftParam ? Number(draftParam) : null;
  const dataLoadedRef = useRef(false);

  useEffect(() => {
    let isMounted = true;

    const loadData = async () => {
      if (dataLoadedRef.current) return;

      if (!draftId) {
        showError("Vahetuse mustandi ID puudub.");
        navigate("/Replace1");
        return;
      }

      try {
        const draft = await fetchMeterReplacementDraft(draftId);
        if (isMounted && draft?.payload) {
          setData(draft.payload);
          dataLoadedRef.current = true;
        }
      } catch (err) {
        console.error("Failed to load replacement draft:", err);
        showError("Mustandi andmete laadimine ebaõnnestus.");
        return;
      }

      try {
        const [brands, qnoms, diams] = await Promise.all([
          fetchMeterBrands(),
          fetchQnoms(),
          fetchDiameters(),
        ]);
        if (!isMounted) return;
        setBrandOptions(brands);
        setQnOptions(qnoms);
        setDiamOptions(diams);
      } catch (err) {
        console.error("Failed to load lookup tables:", err);
      }
    };

    loadData();

    return () => {
      isMounted = false;
    };
  }, [draftId, navigate, showError]);

  const fmtDate = (iso) =>
    iso
      ? new Date(iso).toLocaleDateString("et-EE", {
          day: "2-digit",
          month: "2-digit",
          year: "numeric",
        })
      : "—";

  const actionLabel = data.workType || "—";
  const typeLabel = TYPE_LABELS[data.meterType] || data.meterType || "—";

  const oldBrandName = (() => {
    const id = Number(data.oldBrand);
    const b = brandOptions.find((x) => x.id === id);
    return b ? b.name : data.oldBrand || "—";
  })();

  const newBrandName = (() => {
    const id = Number(data.veemootjaMark);
    const b = brandOptions.find((x) => x.id === id);
    return b ? b.name : data.veemootjaMark || "—";
  })();

  const oldQNomLabel = (() => {
    const id = Number(data.oldQn);
    const q = qnOptions.find((x) => x.id === id);
    return q ? formatDecimal(q.q_nom) : data.oldQn || "—";
  })();

  const newQNomLabel = (() => {
    const id = Number(data.qn);
    const q = qnOptions.find((x) => x.id === id);
    return q ? formatDecimal(q.q_nom) : data.qn || "—";
  })();

  const diamLabel = (() => {
    const id = Number(data.diameter);
    const d = diamOptions.find((x) => x.id === id);
    return d ? formatDecimal(d.diameter) : data.diameter || "—";
  })();

  const onConsoleLabel =
    data.meterIsOnConsole === "Jah"
      ? "Jah"
      : data.meterIsOnConsole === "Ei"
      ? "Ei"
      : "—";

  const handleBackEdit = () => {
    if (draftId) {
      navigate(`/Replace2?draft=${draftId}`);
    } else {
      navigate(-1);
    }
  };

  const handleSendToDatabase = async () => {
    try {

      if (!draftId) {
        showError("Mustandi ID puudub. Palun avage vahetuse mustand uuesti.");
        return;
      }


      const toDateOnly = (dt) => {
        if (!dt) return null;
        try {
          const date = new Date(dt);
          if (isNaN(date.getTime())) return null;
          return date.toISOString().slice(0, 10);
        } catch (e) {
          return null;
        }
      };
      const toBool = (v) => v === true || v === "Jah";

      // Helper function to convert base64 to Blob
      const dataURLtoBlob = (dataURL) => {
        const [header, base64] = dataURL.split(",");
        const mime = header.match(/:(.*?);/)[1];
        const bin = atob(base64);
        const arr = new Uint8Array(bin.length);
        for (let i = 0; i < bin.length; i++) arr[i] = bin.charCodeAt(i);
        return new Blob([arr], { type: mime });
      };

      // Validate required data
      if (!data.oldMeterId) {
        showError("Vana arvesti ID puudub. Palun algake otsast peale.");
        return;
      }

      if (!data.tehaseNumber) {
        showError("Uue arvesti number puudub. Palun täitke kõik väljad.");
        return;
      }

      // Create REMOVE meter report for old meter
      const removePayload = {
        action: "remove",
        client: data.oldClientId,
        estate: data.oldEstateId,
        meter_brand: data.oldBrand ? Number(data.oldBrand) : null,
        meter_number: data.oldMeterNumber?.toString().trim() || "",
        q_nom: data.oldQn ? Number(data.oldQn) : null,
        diameter: data.oldDiameter ? Number(data.oldDiameter) : null,
        type: data.oldType,
        last_certification_date: toDateOnly(data.oldLastCertification),
        reading: Number(normalizeDecimal(data.oldReading)) || 0,
        date: new Date(data.workDate).toISOString(),
        signature: data.signature,
        status: "SIGNED", // Create as SIGNED so it's complete
        // Only include user-provided notes
        notes: data.notes || "",
      };



      // Create ADD meter report for new meter
      const addPayload = {
        action: "add",
        estate: data.estate,
        client: data.client,
        meter_number: data.tehaseNumber?.toString().trim() || "",
        reading: Number(normalizeDecimal(data.arvestiNait)) || 0,
        meter_brand: Number(data.veemootjaMark),
        q_nom: Number(data.qn),
        type: data.meterType || "MAIN_GAUGE",
        diameter:
          data.diameter && Number(data.diameter) > 0
            ? Number(data.diameter)
            : null,
        last_certification_date: toDateOnly(data.viimaneTaatlemine),
        verification_date: toDateOnly(data.sealDate),
        sealing_date: toDateOnly(data.sealDate),
        seal_number: data.sealNumber || null,
        on_console: toBool(data.meterIsOnConsole),
        meter_node_compliant:
          data.meterNodeCompliant === "Jah"
            ? true
            : data.meterNodeCompliant === "Ei"
            ? false
            : null,
        date: new Date(data.workDate).toISOString(),
        signature: data.signature,
        status: "SIGNED", // Create as SIGNED so it's complete
        // Only include user-provided notes
        notes: data.notes || "",
      };



      // Create both meter reports
      let removeResult, addResult;

      try {
        // Create remove report
        removeResult = await createMeterReport(removePayload);


        // Create add report
        addResult = await createMeterReport(addPayload);


        showSuccess("Asendamise aruanded salvestatud edukalt.");
      } catch (reportErr) {
        console.error("Error creating meter reports:", reportErr);
        console.error("Error response:", reportErr.response?.data);

        // More specific error handling
        if (reportErr.response?.status === 400) {
          const errorData = reportErr.response?.data;
          let errorMsg = "Aruande andmetes on viga:\n";

          if (typeof errorData === "object") {
            // Loop through field errors
            for (const [field, errors] of Object.entries(errorData)) {
              if (Array.isArray(errors)) {
                errorMsg += `${field}: ${errors.join(", ")}\n`;
              } else {
                errorMsg += `${field}: ${errors}\n`;
              }
            }
          } else {
            errorMsg += errorData || reportErr.message;
          }

          showError(errorMsg);
        } else {
          showError(
            `Aruannete salvestamisel ilmnes viga: ${
              reportErr.response?.data?.detail || reportErr.message
            }`
          );
        }
        return;
      }

      // Now that both reports are created, use replaceMeter API for the physical meter operations
      if (removeResult?.id && addResult?.id && data.oldMeterId) {
        try {


          await replaceMeter(
            data.oldMeterId,
            addResult.id, // Use the newly created add report ID
            Number(normalizeDecimal(data.oldReading)) || 0,
            removeResult.id // Provide old meter report ID for updating old meter before archive
          );


          showSuccess("Meetri asendamine lõpetatud edukalt!");
        } catch (replaceErr) {
          console.error("Replacement API error:", replaceErr);
          console.error("Error details:", replaceErr.response?.data);

          // Provide more specific error messages
          if (replaceErr.response?.status === 404) {
            showError(
              "Vana arvesti või uus aruanne ei leitud. Kontrollige andmeid."
            );
          } else if (replaceErr.response?.status === 400) {
            const errorMsg =
              replaceErr.response?.data?.error ||
              replaceErr.response?.data?.detail ||
              "Vigased andmed";
            showError(`Vigased andmed: ${errorMsg}`);
          } else if (replaceErr.response?.status === 500) {
            showError(
              "Serveri viga meetri asendamisel. Aruanded on loodud, kuid füüsilist vahetust ei saa lõpetada. Palun võtke ühendust administraatoriga."
            );
          } else {
            showError(
              `Meetri asendamine ebaõnnestus: ${
                replaceErr.response?.data?.error || replaceErr.message
              }`
            );
          }

          // STOP THE WORKFLOW - Do not continue with image upload or navigation if meter replacement fails
          return;
        }
      }

      // Upload images to both reports if any exist
      const uploadPromises = [];

      // Upload old meter images to remove report
      if (removeResult?.id && data.oldImages && data.oldImages.length > 0) {
        let uploadIndex = 1;
        for (const src of data.oldImages) {
          if (!src.startsWith("data:")) continue;

          try {
            const blob = dataURLtoBlob(src);
            const filename = `remove-${removeResult.number}-${uploadIndex}.png`;
            uploadPromises.push(
              uploadMeterReportImage(removeResult.id, blob, filename)
            );
            uploadIndex++;
          } catch (imgErr) {
            console.error(
              `Failed to prepare old meter image ${uploadIndex} for remove report:`,
              imgErr
            );
          }
        }
      }

      // Upload new meter images to add report
      if (addResult?.id && data.newImages && data.newImages.length > 0) {
        let uploadIndex = 1;
        for (const src of data.newImages) {
          if (!src.startsWith("data:")) continue;

          try {
            const blob = dataURLtoBlob(src);
            const filename = `add-${addResult.number}-${uploadIndex}.png`;
            uploadPromises.push(
              uploadMeterReportImage(addResult.id, blob, filename)
            );
            uploadIndex++;
          } catch (imgErr) {
            console.error(
              `Failed to prepare new meter image ${uploadIndex} for add report:`,
              imgErr
            );
          }
        }
      }

      // Execute all uploads
      if (uploadPromises.length > 0) {
        try {
          await Promise.all(uploadPromises);

        } catch (uploadErr) {
          console.error("Some image uploads failed:", uploadErr);
          // Don't fail the whole operation for image upload errors
        }
      }

      try {
        await deleteMeterReplacementDraft(draftId);
      } catch (firstErr) {
        console.error("First draft deletion attempt failed:", firstErr);
        await new Promise((r) => setTimeout(r, 2000));
        try {
          await deleteMeterReplacementDraft(draftId);
        } catch (secondErr) {
          console.error("Second draft deletion attempt failed:", secondErr);
          showError(
            "Mustand jäi kustutamata. Palun kustutage see käsitsi tööde lehelt."
          );
        }
      }

      navigate("/works");
    } catch (err) {
      console.error("Error saving meter replacement reports:", err);
      console.error("Backend response data:", err.response?.data);
      showError(
        `Andmete salvestamisel ilmnes viga: ${
          err.response?.data?.detail ||
          err.response?.data?.message ||
          err.message
        }`
      );
    }
  };

  return (
    <div className="max-w-4xl mx-auto mt-24 p-6 bg-white shadow-lg rounded-lg border border-gray-300">
      <div className="bg-primary text-white text-center py-4 rounded-t-lg">
        <h1 className="text-xl font-bold">Vahetuse lõplik kokkuvõte</h1>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-6">
        {/* Old meter summary */}
        <div className="bg-red-50 p-4 rounded-lg border border-red-200">
          <h2 className="text-lg font-semibold text-red-800 mb-4">
            Eemaldatud arvesti
          </h2>
          <table className="w-full table-auto">
            <tbody>
              <tr>
                <th className="p-2 text-left border-t border-gray-200 text-sm">
                  Arvesti number
                </th>
                <td className="p-2 border-t border-gray-200 text-sm">
                  {data.oldMeterNumber || "—"}
                </td>
              </tr>
              <tr>
                <th className="p-2 text-left border-t border-gray-200 text-sm">
                  Aadress
                </th>
                <td className="p-2 border-t border-gray-200 text-sm">
                  {data.oldAddress || "—"}
                </td>
              </tr>
              <tr>
                <th className="p-2 text-left border-t border-gray-200 text-sm">
                  Lepingupartner
                </th>
                <td className="p-2 border-t border-gray-200 text-sm">
                  {data.oldOwner || "—"}
                </td>
              </tr>
              <tr>
                <th className="p-2 text-left border-t border-gray-200 text-sm">
                  Arvesti mark
                </th>
                <td className="p-2 border-t border-gray-200 text-sm">
                  {oldBrandName}
                </td>
              </tr>
              <tr>
                <th className="p-2 text-left border-t border-gray-200 text-sm">
                  Qn/m³
                </th>
                <td className="p-2 border-t border-gray-200 text-sm">
                  {oldQNomLabel}
                </td>
              </tr>
              <tr>
                <th className="p-2 text-left border-t border-gray-200 text-sm">
                  Lõplik näit
                </th>
                <td className="p-2 border-t border-gray-200 text-sm">
                  {formatDecimal(data.oldReading) || "—"}
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        {/* New meter summary */}
        <div className="bg-green-50 p-4 rounded-lg border border-green-200">
          <h2 className="text-lg font-semibold text-green-800 mb-4">
            Paigaldatud arvesti
          </h2>
          <table className="w-full table-auto">
            <tbody>
              <tr>
                <th className="p-2 text-left border-t border-gray-200 text-sm">
                  Veemõõtja number
                </th>
                <td className="p-2 border-t border-gray-200 text-sm">
                  {data.tehaseNumber || "—"}
                </td>
              </tr>
              <tr>
                <th className="p-2 text-left border-t border-gray-200 text-sm">
                  Kuupäev
                </th>
                <td className="p-2 border-t border-gray-200 text-sm">
                  {fmtDate(data.workDate)}
                </td>
              </tr>
              <tr>
                <th className="p-2 text-left border-t border-gray-200 text-sm">
                  Töö liik
                </th>
                <td className="p-2 border-t border-gray-200 text-sm">
                  {actionLabel}
                </td>
              </tr>
              <tr>
                <th className="p-2 text-left border-t border-gray-200 text-sm">
                  Tüüp
                </th>
                <td className="p-2 border-t border-gray-200 text-sm">
                  {typeLabel}
                </td>
              </tr>
              <tr>
                <th className="p-2 text-left border-t border-gray-200 text-sm">
                  Arvesti number
                </th>
                <td className="p-2 border-t border-gray-200 text-sm">
                  {data.tehaseNumber || "—"}
                </td>
              </tr>
              <tr>
                <th className="p-2 text-left border-t border-gray-200 text-sm">
                  Arvesti mark
                </th>
                <td className="p-2 border-t border-gray-200 text-sm">
                  {newBrandName}
                </td>
              </tr>
              <tr>
                <th className="p-2 text-left border-t border-gray-200 text-sm">
                  Qn/m³
                </th>
                <td className="p-2 border-t border-gray-200 text-sm">
                  {newQNomLabel}
                </td>
              </tr>
              <tr>
                <th className="p-2 text-left border-t border-gray-200 text-sm">
                  Läbimõõt
                </th>
                <td className="p-2 border-t border-gray-200 text-sm">
                  {diamLabel}
                </td>
              </tr>
              <tr>
                <th className="p-2 text-left border-t border-gray-200 text-sm">
                  Algseisu näit
                </th>
                <td className="p-2 border-t border-gray-200 text-sm">
                  {formatDecimal(data.arvestiNait) || "—"}
                </td>
              </tr>
              <tr>
                <th className="p-2 text-left border-t border-gray-200 text-sm">
                  Viimane taatlemine
                </th>
                <td className="p-2 border-t border-gray-200 text-sm">
                  {fmtDate(data.viimaneTaatlemine)}
                </td>
              </tr>
              <tr>
                <th className="p-2 text-left border-t border-gray-200 text-sm">
                  Plommimise kuupäev
                </th>
                <td className="p-2 border-t border-gray-200 text-sm">
                  {fmtDate(data.sealDate)}
                </td>
              </tr>
              <tr>
                <th className="p-2 text-left border-t border-gray-200 text-sm">
                  Plommi number
                </th>
                <td className="p-2 border-t border-gray-200 text-sm">
                  {data.sealNumber || "—"}
                </td>
              </tr>
              <tr>
                <th className="p-2 text-left border-t border-gray-200 text-sm">
                  Konsoolil
                </th>
                <td className="p-2 border-t border-gray-200 text-sm">
                  {onConsoleLabel}
                </td>
              </tr>
              <tr>
                <th className="p-2 text-left border-t border-gray-200 text-sm">
                  Mõõdusõlm vastavuses
                </th>
                <td className="p-2 border-t border-gray-200 text-sm">
                  {data.meterNodeCompliant || "—"}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      {/* Additional details */}
      <table className="w-full table-auto mt-6">
        <tbody>
          {data.notes && (
            <tr>
              <th className="p-2 text-left border-t border-gray-200">
                Märkused
              </th>
              <td className="p-2 border-t border-gray-200">{data.notes}</td>
            </tr>
          )}
          {/* allkiri */}
          <tr>
            <th className="p-2 text-left border-t border-gray-200">Allkiri</th>
            <td className="p-2 border-t border-gray-200">
              {data.signature ? (
                <Popup
                  modal
                  trigger={
                    <img
                      src={data.signature}
                      alt="Allkiri"
                      className="w-24 h-24 object-cover rounded-md cursor-pointer"
                    />
                  }
                  closeOnDocumentClick={false}
                >
                  {(close) => (
                    <div className="relative p-4">
                      <img
                        src={data.signature}
                        alt="Allkiri"
                        className="w-full h-auto max-w-3xl mx-auto"
                      />
                      <button
                        onClick={close}
                        className="absolute top-2 right-2 text-2xl"
                      >
                        <FaTimesCircle />
                      </button>
                    </div>
                  )}
                </Popup>
              ) : (
                <em>Pole allkirja</em>
              )}
            </td>
          </tr>
          {/* vana arvesti pildid */}
          {data.oldImages && data.oldImages.length > 0 && (
            <tr>
              <th className="p-2 text-left border-t border-gray-200">
                Vana arvesti pildid
              </th>
              <td className="p-2 border-t border-gray-200">
                <div className="flex space-x-4 flex-wrap">
                  {data.oldImages.map((img, idx) => (
                    <Popup
                      key={`old-${idx}`}
                      modal
                      trigger={
                        <img
                          src={img}
                          alt={`Vana arvesti pilt ${idx + 1}`}
                          className="w-24 h-24 object-cover rounded-md cursor-pointer border border-red-200"
                        />
                      }
                      closeOnDocumentClick={false}
                    >
                      {(close) => (
                        <div className="relative p-4">
                          <img
                            src={img}
                            alt={`Vana arvesti pilt ${idx + 1}`}
                            className="w-full h-auto max-w-3xl mx-auto"
                          />
                          <button
                            onClick={close}
                            className="absolute top-2 right-2 text-2xl"
                          >
                            <FaTimesCircle />
                          </button>
                        </div>
                      )}
                    </Popup>
                  ))}
                </div>
              </td>
            </tr>
          )}
          {/* uue arvesti pildid */}
          {data.newImages && data.newImages.length > 0 && (
            <tr>
              <th className="p-2 text-left border-t border-gray-200">
                Uue arvesti pildid
              </th>
              <td className="p-2 border-t border-gray-200">
                <div className="flex space-x-4 flex-wrap">
                  {data.newImages.map((img, idx) => (
                    <Popup
                      key={`new-${idx}`}
                      modal
                      trigger={
                        <img
                          src={img}
                          alt={`Uue arvesti pilt ${idx + 1}`}
                          className="w-24 h-24 object-cover rounded-md cursor-pointer border border-green-200"
                        />
                      }
                      closeOnDocumentClick={false}
                    >
                      {(close) => (
                        <div className="relative p-4">
                          <img
                            src={img}
                            alt={`Uue arvesti pilt ${idx + 1}`}
                            className="w-full h-auto max-w-3xl mx-auto"
                          />
                          <button
                            onClick={close}
                            className="absolute top-2 right-2 text-2xl"
                          >
                            <FaTimesCircle />
                          </button>
                        </div>
                      )}
                    </Popup>
                  ))}
                </div>
              </td>
            </tr>
          )}
        </tbody>
      </table>

      <div className="flex justify-between mt-6">
        <button
          onClick={handleBackEdit}
          className="px-4 py-2 bg-secondary text-white rounded-md hover:bg-secondary-hover"
        >
          Tagasi
        </button>
        <button
          onClick={handleSendToDatabase}
          className="bg-primary text-white py-3 px-6 rounded-md hover:bg-primary-hover"
        >
          Kinnita vahetuse akt
        </button>
      </div>
    </div>
  );
}
